import { CheckCircleIcon, RssIcon } from '@heroicons/react/20/solid'
import {
  IconBrandYoutubeFilled,
  IconCalendarEvent,
  IconTicket,
  IconSchool,
  IconSettings,
  IconPlant,
  IconMoodSearch,
  IconMoodDollar,
  IconBolt,
  IconColorFilter,
  IconDeviceComputerCamera,
  IconReportMoney,
  IconEye,
  IconEyeFilled,
  IconCircleDot,
  IconCircleDotFilled,
  IconLetterO,
  IconFlame,
} from '@tabler/icons-react'
import classNames from 'classnames'
import { Tooltip } from 'react-tooltip'

export type DefaultContentPackType = {
  id: string
  label: string
  bgColor: string
  iconColor: string
  textColor: string
  icon: React.ElementType
}

export const DEFAULT_CONTENT_PACK_TYPES: DefaultContentPackType[] = [
  {
    id: 'podcasts',
    label: 'Podcaster',
    bgColor: 'bg-purple-600 hover:bg-purple-700',
    iconColor: 'text-purple-100',
    textColor: 'text-purple-900',
    icon: RssIcon,
  },
  {
    id: 'youtube',
    label: 'YouTuber',
    bgColor: 'bg-crimson-600 hover:bg-crimson-700',
    iconColor: 'text-crimson-100',
    textColor: 'text-crimson-900',
    icon: IconBrandYoutubeFilled,
  },
  {
    id: 'meetings',
    label: 'Meetings',
    bgColor: 'bg-green-600 hover:bg-green-700',
    iconColor: 'text-green-100',
    textColor: 'text-green-900',
    icon: IconCalendarEvent,
  },
  {
    id: 'coaching',
    label: 'Coaching Session',
    bgColor: 'bg-red-600 hover:bg-red-500',
    iconColor: 'text-red-100',
    textColor: 'text-red-900',
    icon: IconPlant,
  },
  {
    id: 'research',
    label: 'Research',
    bgColor: 'bg-yellow-600 hover:bg-yellow-700',
    iconColor: 'text-yellow-100',
    textColor: 'text-yellow-900',
    icon: IconMoodSearch,
  },
  {
    id: 'meetings',
    label: 'Meeting',
    bgColor: 'bg-orange-600 hover:bg-orange-700',
    iconColor: 'text-orange-100',
    textColor: 'text-orange-900',
    icon: IconCalendarEvent,
  },
  {
    id: 'base-pack',
    label: 'Base Pack',
    bgColor: 'bg-gray-600 hover:bg-gray-700',
    iconColor: 'text-gray-100',
    textColor: 'text-gray-900',
    icon: IconCircleDot,
  },
  {
    id: 'keynote',
    label: 'Keynote',
    bgColor: 'bg-indigo-600 hover:bg-indigo-700',
    iconColor: 'text-indigo-100',
    textColor: 'text-indigo-900',
    icon: IconTicket,
  },
  {
    id: 'product-management',
    label: 'Product Management',
    bgColor: 'bg-pink-600 hover:bg-pink-700',
    iconColor: 'text-pink-100',
    textColor: 'text-pink-900',
    icon: IconColorFilter,
  },
  {
    id: 'lecture',
    label: 'Lecture',
    bgColor: 'bg-cyan-600 hover:bg-cyan-700',
    iconColor: 'text-cyan-100',
    textColor: 'text-cyan-900',
    icon: IconSchool,
  },
  {
    id: 'webinar',
    label: 'Webinar',
    bgColor: 'bg-emerald-600 hover:bg-emerald-700',
    iconColor: 'text-emerald-100',
    textColor: 'text-emerald-900',
    icon: IconDeviceComputerCamera,
  },
  {
    id: 'interview',
    label: 'Interview',
    bgColor: 'bg-teal-600 hover:bg-teal-700',
    iconColor: 'text-teal-100',
    textColor: 'text-teal-900',
    icon: IconReportMoney,
  },
  {
    id: 'custom',
    label: 'Custom',
    bgColor: 'bg-toasty-blue-600 hover:bg-toasty-blue-700',
    iconColor: 'text-toasty-blue-100',
    textColor: 'text-toasty-blue-900',
    icon: IconFlame,
  },
]

export const ContentPackTypeCard = ({
  selected,
  setSelectedItem,
  label,
  id,
  description,
  icon: IconComponent,
  bgColor,
  iconColor,
}: {
  selected: boolean
  setSelectedItem: (id: number) => void
  label: string
  description?: string
  id: number
  icon: React.ElementType
  bgColor?: string
  iconColor?: string
}) => {
  return (
    <div
      className="flex h-fit max-h-[100px] w-full flex-col items-center justify-center"
      title={description || ''}
    >
      <div
        data-tooltip-id={`content-pack-type-${id}`}
        onClick={() => setSelectedItem(id)}
        className={classNames(
          'relative flex h-fit w-full cursor-pointer items-center gap-x-2 rounded-lg bg-white px-2.5 py-2.5 shadow-sm transition-all duration-300',
          selected
            ? 'border-2 border-toasty-blue-500'
            : 'border-2 hover:border-gray-400'
        )}
      >
        <div className="flex-shrink-0">
          <div className={classNames('rounded-lg p-1 shadow-inner', bgColor)}>
            <IconComponent className={classNames('h-6 w-6', iconColor)} />
          </div>
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-y-0.5">
          <span aria-hidden="true" className="absolute inset-0" />
          <p className="relative overflow-hidden text-ellipsis whitespace-nowrap text-xs font-semibold text-gray-900">
            {label}
          </p>
        </div>
        <IconCircleDotFilled
          className={classNames(
            'absolute right-1.5 top-1.5 h-3 w-3 text-toasty-blue-600 transition-all duration-300',
            selected ? 'opacity-100' : 'opacity-0'
          )}
        />
      </div>
    </div>
  )
}
